<template>
    <div class="FiltersSidebar">
        <FiltersSidebarToggle
            :isOpen="isOpen"
            :isFiltering="isFiltering"
            :entityColor="entityColor"
            @toggle="$emit('toggle')"
        />
        <div
            ref="filtersSidebar"
            :class="['FiltersSidebarContent', isOpen ? 'SidebarOpen' : 'SidebarClosed']"
        >
            <div class="Columns">
                <div class="SearchFilter">
                    <WInput
                        v-model="searchingFilter"
                        type="text"
                        name="searchingFilter"
                        iconLeft="search"
                        iconColor="grayMedium"
                        :placeholder="$t('filters.search')"
                    />
                    <WIcon
                        icon="x"
                        :size="16"
                        :clickable="!!searchingFilter"
                        @click="searchingFilter = ''"
                    />
                </div>
                <Collapse
                    v-for="(column, index) in finalColumns"
                    :key="index"
                    :opened="checkOpen(column)"
                    class="GroupCollapse"
                >
                    <template #header>
                        <div
                            class="CollapseHeader"
                            @click="toggleCollapse(column)"
                        >
                            <div class="GroupName">
                                <WIcon
                                    v-if="column.icon"
                                    color="accent-blue"
                                    :icon="column.icon"
                                    :size="20"
                                />
                                <WText
                                    color="accent-blue"
                                    :size="16"
                                >
                                    {{ column.headerName }}
                                </WText>
                            </div>
                            <div class="HeaderTitle">
                                <div
                                    v-if="getAppliedFiltersQty(column) > 0"
                                    :class="['AppliedQty', entityColor]"
                                >
                                    <WText
                                        color="white"
                                        weight="bold"
                                        :size="12"
                                    >
                                        {{ getAppliedFiltersQty(column) }}
                                    </WText>
                                </div>
                                <WIcon
                                    color="accent-blue"
                                    :icon="checkOpen(column) ? 'chevron-up1' : 'chevron-down1'"
                                    :size="20"
                                />
                            </div>
                        </div>
                    </template>
                    <template #default>
                        <Fields
                            :fields="filterBoolFields(column.children)"
                            :filterModel="filterModel"
                            :selectGroups="selectGroups"
                            :entityColor="entityColor"
                            @cleanFilter="handleCleanFilter"
                            @applyFilter="handleApplyFilter"
                        />
                    </template>
                </Collapse>
            </div>
            <div class="ButtonsWrapper">
                <div class="ButtonsContainer">
                    <WButton
                        outline
                        color="accent-blue"
                        class="Button"
                        iconLeft="trash"
                        :iconSize="16"
                        :disabled="!isFiltering"
                        @click="$emit('cleanAllFilters')"
                    >
                        {{ $t('filters.clean') }}
                    </WButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { intersection } from 'lodash'
import { Collapse } from '@/components'
import { ENTITIES_BY_ID } from '@/entities/entities'
import Fields from './Fields.vue'
import FiltersSidebarToggle from './FiltersSidebarToggle.vue'

export default {
    name: 'FiltersSidebar',
    components: { Collapse, Fields, FiltersSidebarToggle },
    props: {
        entityKey: VueTypes.string,
        isOpen: VueTypes.bool,
        columnDefs: VueTypes.array,
        selectGroups: VueTypes.object,
        filterModel: VueTypes.object,
    },
    data () {
        return {
            openColumn: '',
            searchingFilter: '',
        }
    },
    computed: {
        finalColumns () {
            const { columnDefs, searchingFilter, filterBoolFields } = this
            const searching = searchingFilter.toLowerCase()
            return columnDefs.map((c) => {
                const matchColumn = c.headerName.toLowerCase().includes(searching)
                const filteredChildren = filterBoolFields(c.children).filter(field => field.headerName.toLowerCase().includes(searching))
                return { ...c, children: matchColumn ? c.children : filteredChildren }
            }).filter(c => c.children.length > 0)
        },
        isFiltering () {
            const filterModelFields = Object.keys(this.filterModel)
            return !!filterModelFields.length
        },
        entityColor () {
            return ENTITIES_BY_ID[this.entityKey].color
        },
    },
    watch: {
        finalColumns (newCols, prevCols) {
            const altValue = prevCols.length === 1 ? '' : this.openColumn
            this.openColumn = newCols.length === 1 ? newCols[0].headerName : altValue
        },
    },
    methods: {
        toggleCollapse (col) {
            this.openColumn = this.checkOpen(col) ? '' : col.headerName
        },
        checkOpen ({ headerName }) {
            return this.openColumn === headerName
        },
        filterBoolFields (children) {
            return children.filter(({ type }) => type !== 'bool')
        },
        handleCleanFilter (field) {
            this.$emit('cleanFilter', field)
        },
        handleApplyFilter (field, model) {
            this.$emit('applyFilter', field, model)
        },
        getAppliedFiltersQty ({ children }) {
            const fieldsBeingApplied = Object.keys(this.filterModel)
            const columnFields = children.map(f => f.field)
            const shared = intersection(fieldsBeingApplied, columnFields)
            return shared.length
        },
    },
}
</script>

<style lang="scss" scoped>
.FiltersSidebar {
    display: flex;
    flex-direction: row;
    border-radius: $border-radius;
    margin-right: 10px;
    .FiltersSidebarContent{
        background-color: white;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        transition: width 0.1s, opacity 0.1s, margin-left 0.1s;
        overflow-x: hidden;
        &.SidebarOpen {
            width: 17vw;
            opacity: 1;
            margin-left: 10px;
        }
        &.SidebarClosed {
            width: 0vw;
            opacity: 0;
        }
        .Columns {
            padding: 0 15px;
            overflow-y: auto;
            flex: 1 1 auto;
            height: 0px;
            .SearchFilter {
                display: flex;
                flex: 1;
                align-items: center;
                margin: 15px 0 10px 0;
                .WInput {
                    flex: 1;
                    margin-right: 10px;
                    &::v-deep .WIcon {
                        top: 45%;
                    }
                }
            }
        }
    }
}
.GroupCollapse {
    border-bottom: 1px solid $accent-blue;
}
.CollapseHeader {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding-top: 14px;
    padding-bottom: 14px;
    .GroupName {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
    }
}
.ButtonsWrapper {
    box-sizing: border-box;
    padding: 15px;
    width: 100%;
    .ButtonsContainer {
        border-top: 1px solid $gray-04;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-top: 15px;
    }
}
.HeaderTitle {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    .AppliedQty {
        background-color: $danger;
        margin-left: 8px;
        margin-right: 4px;
        border-radius: 100%;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        &.turquoise {
            background-color: $turquoise;
        }
        &.cyan {
            background-color: $cyan;
        }
        &.violet {
            background-color: $violet;
        }
        &.pink {
            background-color: $pink;
        }
    }
}
</style>
